import { useState, useEffect, useMemo } from 'react';
import { useBusiness } from '@/contexts/BusinessContext';
import { useMonth } from '@/contexts/MonthContext';
import { getExpenses, createExpense, deleteExpense, updateExpense } from '@/lib/api';
import type { Expense, ExpenseCategory } from '@/types/database';
import { formatDate, getMonthRange, getMonthName } from '@/lib/dateUtils';
import { Receipt, Plus, CreditCard as Edit2, Trash2, Calendar, X, Search, ChevronDown } from 'lucide-react';
import InfoTooltip from '@/components/InfoTooltip';
import LogoSpinner from '@/components/LogoSpinner';

export default function ExpensesPage() {
  const { selectedBusiness } = useBusiness();
  const { selectedMonth, selectMonth } = useMonth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<Expense | null>(null);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [formData, setFormData] = useState({
    amountMvr: '',
    category: 'Personal' as ExpenseCategory,
    note: '',
    date: formatDate(new Date()),
  });

  useEffect(() => { loadExpenses(); }, [selectedBusiness, selectedMonth]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  async function loadExpenses() {
    if (!selectedBusiness) return;
    try {
      setLoading(true);
      const { start, end } = getMonthRange(selectedMonth.year, selectedMonth.month);
      const data = await getExpenses(selectedBusiness.id, {
        start: start.split('T')[0],
        end: end.split('T')[0],
      });
      setExpenses(data);
    } catch (error: any) {
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  const filteredExpenses = useMemo(() => {
    if (!debouncedSearch.trim()) return expenses;
    const query = debouncedSearch.toLowerCase();
    return expenses.filter(expense =>
      expense.category.toLowerCase().includes(query) ||
      expense.note.toLowerCase().includes(query) ||
      (Number(expense.amount_mvr) / 100).toString().includes(query)
    );
  }, [expenses, debouncedSearch]);

  function openAddModal() {
    setEditingExpense(null);
    setFormData({ amountMvr: '', category: 'Personal', note: '', date: formatDate(new Date()) });
    setModalVisible(true);
  }

  function openEditModal(expense: Expense) {
    setEditingExpense(expense);
    setFormData({
      amountMvr: (Number(expense.amount_mvr) / 100).toString(),
      category: expense.category,
      note: expense.note,
      date: expense.expense_date,
    });
    setModalVisible(true);
  }

  async function handleSave() {
    if (!selectedBusiness) return;
    const amount = parseFloat(formData.amountMvr);
    if (isNaN(amount) || amount <= 0) { alert('Please enter a valid amount'); return; }
    try {
      setLoading(true);
      if (editingExpense) {
        await updateExpense(editingExpense.id, {
          amount_mvr: amount * 100, category: formData.category, note: formData.note, expense_date: formData.date,
        });
      } else {
        await createExpense(selectedBusiness.id, amount * 100, formData.category, formData.note, formData.date);
      }
      await loadExpenses();
      setModalVisible(false);
    } catch (error: any) {
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  function handleDelete(expense: Expense) { setExpenseToDelete(expense); setConfirmDialogVisible(true); }

  async function confirmDelete() {
    if (!expenseToDelete) return;
    try {
      setConfirmDialogVisible(false);
      setLoading(true);
      await deleteExpense(expenseToDelete.id);
      await loadExpenses();
      setExpenseToDelete(null);
    } catch (error: any) {
      alert(`Failed to delete: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  const year = selectedMonth.year;
  const month = selectedMonth.month;
  const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
  const now = new Date();
  const currentYear = now.getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear + 1];

  const totalExpenses = expenses.reduce((sum, e) => sum + (Number(e.amount_mvr) / 100), 0);
  const categories: ExpenseCategory[] = ['Loan', 'Salary', 'Personal', 'Bank Fee', 'Other'];

  const categoryTotals = categories.reduce((acc, cat) => {
    acc[cat] = expenses.filter(e => e.category === cat).reduce((s, e) => s + Number(e.amount_mvr) / 100, 0);
    return acc;
  }, {} as Record<string, number>);

  const avgExpense = expenses.length > 0 ? totalExpenses / expenses.length : 0;

  const expenseSummaryCards = [
    { label: 'Total Expenses', value: `MVR ${totalExpenses.toFixed(2)}`, tooltip: { title: 'Total Expenses', description: `Sum of all ${expenses.length} expense entries this month across all categories (Loan, Salary, Personal, Bank Fee, Other).` } },
    { label: 'Avg Per Entry', value: `MVR ${avgExpense.toFixed(2)}`, tooltip: { title: 'Average Expense', description: 'Average amount per expense entry. Useful for spotting unusually large or small entries.' } },
    { label: 'Largest Category', value: (() => { const max = Object.entries(categoryTotals).sort((a, b) => b[1] - a[1])[0]; return max ? max[0] : 'N/A'; })(), tooltip: { title: 'Largest Category', description: 'The expense category with the highest total spending this month. Review this to understand where most money is going.' } },
    { label: 'Entry Count', value: expenses.length.toString(), tooltip: { title: 'Entry Count', description: 'Total number of expense records entered for the selected month.' } },
  ];

  const getCategoryColor = (category: ExpenseCategory): string => {
    switch (category) {
      case 'Loan': return 'bg-red-500/10 text-red-700 ring-1 ring-inset ring-red-100';
      case 'Salary': return 'bg-teal-500/10 text-teal-700 ring-1 ring-inset ring-teal-100';
      case 'Personal': return 'bg-emerald-500/10 text-emerald-700 ring-1 ring-inset ring-emerald-100';
      case 'Bank Fee': return 'bg-sky-500/10 text-sky-700 ring-1 ring-inset ring-sky-100';
      case 'Other': return 'bg-amber-500/10 text-amber-700 ring-1 ring-inset ring-amber-100';
      default: return 'glass-inner text-white ring-1 ring-inset ring-slate-100';
    }
  };

  const getCategoryButtonColor = (category: ExpenseCategory, isActive: boolean): string => {
    if (isActive) {
      switch (category) {
        case 'Loan': return 'bg-red-500 text-white border-red-500';
        case 'Salary': return 'bg-emerald-800 text-white border-emerald-800';
        case 'Personal': return 'bg-green-500 text-white border-green-500';
        case 'Bank Fee': return 'bg-sky-500 text-white border-sky-500';
        case 'Other': return 'bg-orange-500 text-white border-orange-500';
        default: return 'bg-gray-500 text-white border-gray-500';
      }
    } else {
      switch (category) {
        case 'Loan': return 'bg-white text-red-500 border-red-500 hover:bg-red-500/10';
        case 'Salary': return 'bg-white text-teal-600 border-emerald-800 hover:bg-teal-500/10';
        case 'Personal': return 'bg-white text-green-700 border-green-500 hover:bg-green-50';
        case 'Bank Fee': return 'bg-white text-sky-500 border-sky-500 hover:bg-sky-500/10';
        case 'Other': return 'bg-white text-orange-500 border-orange-500 hover:bg-orange-50';
        default: return 'bg-white text-white border-gray-500 hover:bg-gray-50';
      }
    }
  };

  if (loading && expenses.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <LogoSpinner size={48} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="glass-header shrink-0 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between mb-3 gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-white">Expenses</h1>
            <p className="text-xs text-white mt-0.5">
              Total: <span className="font-semibold text-white">MVR {totalExpenses.toFixed(2)}</span>
              <span className="ml-1.5">&middot; {getMonthName(month)} {year}</span>
            </p>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <button
              onClick={() => setMonthDropdownOpen(true)}
              className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 glass-button rounded-xl text-xs font-medium text-white"
              title={`${getMonthName(month)} ${year}`}
            >
              <Calendar size={14} className="sm:hidden text-teal-400" />
              <span className="hidden sm:inline">{getMonthName(month)} {year}</span>
              <ChevronDown size={13} className="hidden sm:inline" />
            </button>
            <button
              onClick={openAddModal}
              className="flex items-center gap-1.5 p-2 sm:px-4 sm:py-2 glass-button-active text-white rounded-xl text-xs font-medium"
            >
              <Plus size={14} />
              <span className="hidden sm:inline">Add Expense</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-white" size={14} />
          <input
            type="text"
            placeholder="Search expenses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-white/10 border border-white/10 rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 placeholder:text-white"
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-5">
        {expenses.length > 0 && (
          <div className="grid grid-cols-2 fold:grid-cols-4 gap-3 mb-4">
            {expenseSummaryCards.map(card => (
              <InfoTooltip key={card.label} title={card.tooltip.title} description={card.tooltip.description} position="bottom">
                <div className="glass-panel rounded-2xl p-4 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 cursor-default">
                  <p className="text-[11px] font-medium text-white mb-1">{card.label}</p>
                  <p className="text-base font-bold text-white">{card.value}</p>
                </div>
              </InfoTooltip>
            ))}
          </div>
        )}

        {filteredExpenses.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="bg-white/10 rounded-2xl p-5 mb-4">
              <Receipt size={32} className="text-white" />
            </div>
            <h3 className="text-sm font-semibold text-white mb-1">
              {searchQuery ? 'No expenses found' : 'No expenses yet'}
            </h3>
            <p className="text-xs text-white mb-5">
              {searchQuery ? 'Try adjusting your search' : 'Track your business expenses here'}
            </p>
            {!searchQuery && (
              <button
                onClick={openAddModal}
                className="flex items-center gap-1.5 px-4 py-2 glass-button-active text-white rounded-xl text-xs font-medium"
              >
                <Plus size={14} />
                <span>Add First Expense</span>
              </button>
            )}
          </div>
        ) : (
          <div className="glass-panel rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/20">
              <thead className="bg-white/20">
                <tr>
                  <th className="px-4 py-3 text-left text-[11px] font-medium text-white uppercase tracking-wider">Category</th>
                  <th className="px-4 py-3 text-left text-[11px] font-medium text-white uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-[11px] font-medium text-white uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-[11px] font-medium text-white uppercase tracking-wider">Notes</th>
                  <th className="px-4 py-3 text-right text-[11px] font-medium text-white uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/20">
                {filteredExpenses.map(expense => (
                  <tr key={expense.id} className="glass-hover">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(expense.category)}`}>
                          {expense.category}
                        </span>
                        {expense.fee_source && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-white/10 text-white uppercase tracking-wide">
                            {expense.fee_source}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-semibold text-white">
                        MVR {(Number(expense.amount_mvr) / 100).toFixed(2)}
                      </div>
                      {expense.amount_usd != null && (
                        <div className="text-[10px] text-white">
                          ${Number(expense.amount_usd).toFixed(2)} USD
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center text-xs text-white">
                        <Calendar size={12} className="mr-1.5 text-white" />
                        {formatDate(expense.expense_date)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-white max-w-xs truncate">
                        {expense.note || <span className="text-white italic">No notes</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      {expense.fee_source ? (
                        <span className="text-[10px] text-white italic">Auto-synced</span>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEditModal(expense)}
                            className="text-white hover:text-teal-600 p-1.5 rounded-lg glass-hover-teal"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(expense)}
                            className="text-white hover:text-red-600 p-1.5 rounded-lg glass-hover-red"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {modalVisible && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="glass-modal rounded-2xl max-w-sm w-full ">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h2 className="text-base font-bold text-white">{editingExpense ? 'Edit Expense' : 'Add Expense'}</h2>
              <button
                onClick={() => setModalVisible(false)}
                className="text-white p-1 rounded-lg glass-hover"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">Amount (MVR) *</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Enter amount"
                  value={formData.amountMvr}
                  onChange={(e) => setFormData({ ...formData, amountMvr: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/10 rounded-xl text-sm text-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 placeholder:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">Category</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => setFormData({ ...formData, category })}
                      className={`px-3 py-1.5 rounded-xl border text-xs font-medium transition-all duration-200 ${getCategoryButtonColor(category, formData.category === category)}`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">Notes</label>
                <textarea
                  placeholder="Optional notes"
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/10 rounded-xl text-sm text-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 resize-none placeholder:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/10 rounded-xl text-sm text-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 [color-scheme:dark]"
                />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-white/10">
              <button
                onClick={() => setModalVisible(false)}
                className="flex-1 px-4 py-2.5 text-sm glass-button text-white rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-2.5 text-sm glass-button-active text-white rounded-xl font-medium"
              >
                {editingExpense ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {monthDropdownOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="glass-modal rounded-2xl w-full max-w-sm max-h-[50vh] flex flex-col">
            <div className="flex justify-between items-center p-5 border-b border-white/10">
              <h2 className="text-base font-bold text-white">Select Month</h2>
              <button onClick={() => setMonthDropdownOpen(false)} className="p-1.5 rounded-lg glass-hover">
                <X size={18} className="text-white" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              {yearOptions.map(y => (
                <div key={y}>
                  <p className={`text-[11px] font-bold uppercase tracking-wider px-2 pt-3 pb-1 ${y === year ? 'text-teal-400' : 'text-white/50'}`}>{y}</p>
                  {MONTHS.map(m => {
                    const isActive = y === year && m === month;
                    return (
                      <button
                        key={`${y}-${m}`}
                        onClick={() => { selectMonth({ year: y, month: m }); setMonthDropdownOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 rounded-xl text-sm mb-0.5 transition-all duration-150 ${
                          isActive ? 'bg-teal-500/10 text-teal-400 font-semibold' : 'text-white glass-hover'
                        }`}
                      >
                        {getMonthName(m)}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDialogVisible && expenseToDelete && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="glass-modal rounded-2xl max-w-sm w-full p-5 ">
            <h2 className="text-base font-bold text-white mb-1">Delete Expense</h2>
            <p className="text-xs text-white mb-4">Are you sure? This action cannot be undone.</p>
            <div className="glass-inner rounded-xl p-3.5 mb-5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-white">Amount:</span>
                <span className="text-sm font-bold text-white">MVR {(Number(expenseToDelete.amount_mvr) / 100).toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-white">Category:</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${getCategoryColor(expenseToDelete.category)}`}>{expenseToDelete.category}</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setConfirmDialogVisible(false); setExpenseToDelete(null); }}
                className="flex-1 px-4 py-2.5 text-sm glass-button text-white rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2.5 text-sm bg-red-600 text-white rounded-xl glass-hover-red font-medium shadow-sm shadow-red-600/20"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
